import { getMeasuredValue } from '../../miniscreen-importer/utils/info-parsing';
import { parseDate } from '../../miniscreen-importer/utils/parsing';
import { ExtractionResult, MeasuredValue } from './types';
import { validateFhirData } from './validation';

// ToDo: change when get ptslab data
const startDateCode = 'start';
const endDateCode = 'end';

export function getStartDateFromMeasuredValues(
  measuredValues: MeasuredValue[],
) {
  const value = getMeasuredValue(measuredValues, startDateCode, 'start date');
  return parseDate(value);
}

export function getEndDateFromFromMeasuredValues(
  measuredValues: MeasuredValue[],
) {
  const value = getMeasuredValue(measuredValues, endDateCode, 'end date');
  return parseDate(value);
}

function jsonParse(jsonString: string): Record<string, any> | null {
  try {
    const json = JSON.parse(jsonString);

    if (!json || typeof json !== 'object' || Array.isArray(json)) {
      return null;
    }

    return json;
  } catch {
    return null;
  }
}

function getUnit(observation: any): string {
  if (observation.valueQuantity?.unit) {
    return observation.valueQuantity.unit;
  }
  return '';
}

function getValueAndUnit(obs: any): { value: string; unit: string } {
  if (obs.valueQuantity) {
    return {
      value: obs.valueQuantity.value?.toString() ?? '',
      unit: obs.valueQuantity.unit ?? '',
    };
  }
  if (obs.valueString) {
    return {
      value: obs.valueString,
      unit: getUnit(obs),
    };
  }
  if (obs.valueInteger !== undefined && obs.valueInteger !== null) {
    return {
      value: obs.valueInteger.toString(),
      unit: getUnit(obs),
    };
  }
  if (obs.valueBoolean !== undefined) {
    return {
      value: obs.valueBoolean ? 'true' : 'false',
      unit: '',
    };
  }
  if (obs.valueDateTime) {
    return {
      value: obs.valueDateTime,
      unit: getUnit(obs),
    };
  }
  if (obs.valueTime) {
    return {
      value: obs.valueTime,
      unit: getUnit(obs),
    };
  }
  return { value: '', unit: '' };
}

export function parseData(data: string): ExtractionResult {
  const parsedData = jsonParse(data);
  if (parsedData === null) {
    return {
      measurements: [],
      validation: {
        isValid: false,
        error: 'Invalid JSON: file must contain a valid JSON object',
      },
    };
  }

  const validation = validateFhirData(parsedData);

  if (!validation.isValid) {
    return { measurements: [], validation };
  }

  // get observations
  const measurements: MeasuredValue[] = [];
  let observations: any[] = [];

  if (parsedData.resourceType === 'Bundle') {
    observations =
      parsedData.entry
        ?.filter(
          (entry: any) => entry?.resource?.resourceType === 'Observation',
        )
        .map((entry: any) => entry.resource) || [];
  } else if (parsedData.resourceType === 'Observation') {
    observations = [parsedData];
  }

  // get measurement values
  for (const observation of observations) {
    const code = observation.code?.coding?.[0]?.code;
    if (!code) continue;

    const name =
      observation.code.coding[0].display || observation.code.text || '';

    const { value, unit } = getValueAndUnit(observation);

    if (value) {
      measurements.push({ key: code, name, value, unit });
    }

    // get components values
    if (observation.component && Array.isArray(observation.component)) {
      for (const comp of observation.component) {
        const compCode = comp.code?.coding?.[0]?.code || '';
        if (!compCode) continue;

        const compName =
          comp.code?.coding?.[0]?.display ||
          comp.code?.text ||
          `${name} component`;

        const { value: compValue, unit: compUnit } = getValueAndUnit(comp);

        if (compValue) {
          measurements.push({
            key: compCode,
            name: compName,
            value: compValue,
            unit: compUnit,
          });
        }
      }
    }
  }

  // Check if no measurements extracted
  if (measurements.length === 0) {
    return {
      measurements: [],
      validation: {
        isValid: false,
        error: 'No measurements with valid codes found in the file',
      },
    };
  }

  return {
    measurements,
    validation: { isValid: true },
  };
}

export function parseFhirTime(value: string): number {
  const [hours, minutes, seconds] = value.split(':');
  return (
    (parseInt(hours) * 3600 + parseInt(minutes) * 60 + parseInt(seconds)) * 1000
  );
}
